import { Button, Divider, LinearProgress, Stack } from "@mui/material";
import React, { FC, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { ErrorTypography, FormField } from "../../styled/formComponents";
import { Form, Formik } from "formik";

import { UserApi } from "../../../api/userApi";
import { theme } from "../../../styles/themeProvider";
import GoogleLogin from "./GoogleLogin";
import {
	BusinessDataContext,
	BusinessDataContextValues,
	LoginContext,
	LoginContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import { User } from "../../../types/entities";
import { findComponentStep } from "../../../utils/filter";
import { UpdatePasswordButton } from "../../buttons/UpdatePasswordButton";
import { LoginButton } from "../../buttons/LoginButton";
import { CustomException } from "../../../types/customException";
import { replaceKeysInMessage } from "../../../utils/replaceKeys";
import { tryCatchHandler } from "../../../utils/tryCatchHandler";

interface RegisterValues {
	id?: string;
	name: string;
	lastname: string;
	phoneNumber: string;
	email: string;
	password: string;
	confirmPassword: string;
}

interface RegisterProps {
	setSuccess: Function;
	setGoogleError: Function;
	setHeaderTitle?: Function;
	isUpdating?: boolean;
}

const Register: FC<RegisterProps> = ({
	setSuccess,
	setGoogleError,
	setHeaderTitle,
	isUpdating = false,
}) => {
	const { onChangeStep, onChangeLogin, reservation } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { selectedFlow } = useContext(
		BusinessDataContext
	) as BusinessDataContextValues;

	const {
		onLogin,
		onLoginWithGoogle,
		isCalledFromProfile,
		isLoggedWithGoogle,
	} = useContext(LoginContext) as LoginContextValues;

	const { formatMessage } = useIntl();
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	if (isLoggedWithGoogle && setHeaderTitle) {
		setHeaderTitle(
			formatMessage({
				id: "register.updatePhoneTitle",
				defaultMessage: "Por favor, completa tus datos",
			})
		);
	}

	const initialValues = {
		id: reservation.customer.id,
		name: reservation.customer.name ?? "",
		lastname: reservation.customer.lastname ?? "",
		phoneNumber: reservation.customer.phoneNumber ?? "",
		email: reservation.customer.email ?? "",
		password: "",
		confirmPassword: "",
	};

	const requiredMessage = formatMessage({
		id: "error.required",
		defaultMessage: "Campo requerido.",
	});

	const invalidFormat = formatMessage({
		id: "error.invalidFormat",
		defaultMessage: "Formato inválido.",
	});

	const registerSchema = Yup.object().shape({
		name: Yup.string()
			.trim()
			.matches(/^[A-Za-zÁÉÍÓÚáéíóú\s]+$/, {
				message: invalidFormat,
				excludeEmptyString: true,
			})
			.required(requiredMessage),
		lastname: Yup.string()
			.trim()
			.matches(/^[A-Za-zÁÉÍÓÚáéíóú\s]+$/, {
				message: invalidFormat,
				excludeEmptyString: true,
			})
			.required(requiredMessage),
		phoneNumber: Yup.string()
			.trim()
			.matches(/^[0-9\s()+-]+$/, {
				message: formatMessage({
					id: "error.phoneNumber",
					defaultMessage: "El teléfono no puede contener letras.",
				}),
				excludeEmptyString: true,
			})
			.required(requiredMessage),
		email: Yup.string()
			.trim()
			.required(requiredMessage)
			.email(
				formatMessage({
					id: "error.email",
					defaultMessage: "Debe ingresar un mail válido.",
				})
			),
		password: Yup.string()
			.required(requiredMessage)
			.min(
				8,
				formatMessage({
					id: "error.password",
					defaultMessage: "La contraseña debe tener al menos 8 caracteres.",
				})
			),
		confirmPassword: Yup.string()
			.oneOf(
				[Yup.ref("password"), undefined],
				formatMessage({
					id: "error.confirmPassword",
					defaultMessage: "Las contraseñas deben coincidir.",
				})
			)
			.required(requiredMessage),
	});

	const registerSchemaWithGoogle = Yup.object().shape({
		phoneNumber: Yup.string()
			.trim()
			.matches(/^[0-9\s()+-]+$/, {
				message: formatMessage({
					id: "error.phoneNumber",
					defaultMessage: "El teléfono no puede contener letras.",
				}),
				excludeEmptyString: true,
			})
			.required(requiredMessage),
	});

	const handleRegister = async (values: RegisterValues) => {
		setLoading(true);
		setError("");
		const userApi = new UserApi();
		
		const errorMessage = await tryCatchHandler(
			async () => {
				const customer: User = {
					...values,
				};
				let newCustomer;
				if (!customer.id) newCustomer = (await userApi.register(customer)).data;
				else newCustomer = (await userApi.update(customer)).data;

				setSuccess(
					!customer.id
						? formatMessage({
								id: "register.success",
								defaultMessage: "¡Usuario registrado correctamente!",
						  })
						: formatMessage({
								id: "register.updateSuccess",
								defaultMessage: "¡Usuario actualizado correctamente!",
						  })
				);

				onChangeLogin(newCustomer);
				if (isLoggedWithGoogle) onLoginWithGoogle();
				else onLogin();

				let nextStep;
				if (isCalledFromProfile) {
					nextStep = findComponentStep(selectedFlow!, "Profile");
				} else {
					nextStep = findComponentStep(selectedFlow!, "Details");
				}
				onChangeStep(nextStep);
			},
			"register.genericError",
			"Ha ocurrido un error",
			formatMessage
		);

		if (errorMessage) setError(errorMessage);

		setLoading(false);
	};

	return (
		<Stack
			sx={{ width: { mobile: "85%", xs: "75%", sm: "90%", md: "85%" } }}
			spacing={2}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={
					isLoggedWithGoogle || isUpdating
						? registerSchemaWithGoogle
						: registerSchema
				}
				onSubmit={async (values) => await handleRegister(values)}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
				}) => (
					<Form style={{ width: "100%", alignItems: "center" }}>
						<Stack spacing={3}>
							<Stack
								spacing={{ mobile: 3, sm: 2 }}
								direction={{ mobile: "column", sm: "row" }}
							>
								<FormField
									name="name"
									label="Nombre"
									autoComplete="given-name"
									required
									disabled={isLoggedWithGoogle && !isUpdating}
									value={values.name}
									error={errors.name}
									touched={touched.name}
									handleChange={handleChange}
									handleBlur={handleBlur}
								/>

								<FormField
									name="lastname"
									label="Apellido"
									autoComplete="family-name"
									required
									disabled={isLoggedWithGoogle && !isUpdating}
									value={values.lastname}
									error={errors.lastname}
									touched={touched.lastname}
									handleChange={handleChange}
									handleBlur={handleBlur}
								/>
							</Stack>

							<Stack
								spacing={{ mobile: 3, sm: 2 }}
								direction={{ mobile: "column", sm: "row" }}
							>
								<FormField
									name="phoneNumber"
									label="Teléfono"
									required
									autoFocus={isLoggedWithGoogle && !isUpdating}
									value={values.phoneNumber}
									error={errors.phoneNumber}
									touched={touched.phoneNumber}
									handleChange={handleChange}
									handleBlur={handleBlur}
									tooltip={
										isLoggedWithGoogle
											? formatMessage({
													id: "field.completePhone",
													defaultMessage: "Por favor, completa tu teléfono",
											  })
											: undefined
									}
								/>
								<FormField
									name="email"
									label="Email"
									type="email"
									required
									disabled={isLoggedWithGoogle}
									value={values.email}
									error={errors.email}
									touched={touched.email}
									handleChange={handleChange}
									handleBlur={handleBlur}
								/>
							</Stack>

							{!isLoggedWithGoogle && !isUpdating && (
								<Stack
									spacing={{ mobile: 3, sm: 2 }}
									direction={{ mobile: "column", sm: "row" }}
								>
									<FormField
										name="password"
										label="Contraseña"
										type="password"
										required
										autoComplete="current-password"
										value={values.password}
										error={errors.password}
										touched={touched.password}
										handleChange={handleChange}
										handleBlur={handleBlur}
									/>
									<FormField
										name="confirmPassword"
										label="Confirmar contraseña"
										type="password"
										required
										autoComplete="new-password"
										value={values.confirmPassword}
										error={errors.confirmPassword}
										touched={touched.confirmPassword}
										handleChange={handleChange}
										handleBlur={handleBlur}
									/>
								</Stack>
							)}
							{error && <ErrorTypography>{error}</ErrorTypography>}
							{loading && <LinearProgress />}
						</Stack>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={isSubmitting || areEquals(values, reservation.customer)}
							sx={{ height: 50, mt: 4 }}
							onClick={(event) => {
								event.preventDefault();
								handleSubmit();
							}}
						>
							{isUpdating ? (
								<FormattedMessage
									id="register.update"
									defaultMessage="Editar"
								/>
							) : isLoggedWithGoogle ? (
								<FormattedMessage
									id="register.enter"
									defaultMessage="Confirmar"
								/>
							) : (
								<FormattedMessage
									id="register.register"
									defaultMessage="Registrarse"
								/>
							)}
						</Button>
					</Form>
				)}
			</Formik>
			{!isLoggedWithGoogle && !isUpdating && (
				<>
					<Divider
						sx={{
							height: "2px",
							backgroundColor: theme.palette.disabled!.light,
						}}
					/>
					<GoogleLogin setError={setGoogleError} />
				</>
			)}
			{!isUpdating ? (
				<Stack
					direction={{ mobile: "column", xs: "row" }}
					alignSelf="flex-end"
					alignItems={{ mobile: "flex-end", xs: "center" }}
					marginTop={1}
				>
					<LoginButton />
				</Stack>
			) : (
				!isLoggedWithGoogle && <UpdatePasswordButton />
			)}
		</Stack>
	);
};

const areEquals = (values: RegisterValues, customer: User) => {
	return (
		customer.name === values.name &&
		customer.lastname === values.lastname &&
		customer.email === values.email &&
		customer.phoneNumber === values.phoneNumber
	);
};

export default Register;
